import type { NavKey } from "../Sidebar";
import { auth, db } from "../../firebaseClient";
import { canViewData } from "../utils/dataAccess";

export type SearchHit = {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  navKey: NavKey;
  sourceId?: string;
};

type SearchDoc = { id: string; [k: string]: any };

export type SearchScope =
  | "contracts"
  | "legacyContracts"
  | "cases"
  | "disputes"
  | "brandAssets"
  | "lawyers"
  | "templates"
  | "govInbound"
  | "governance"
  | "todos"
  | "approvalRequests";

export type GlobalSearchOptions = {
  scopes?: SearchScope[];
};

const ALL_SCOPES: SearchScope[] = [
  "contracts",
  "legacyContracts",
  "cases",
  "disputes",
  "brandAssets",
  "lawyers",
  "templates",
  "govInbound",
  "governance",
  "todos",
  "approvalRequests",
];

/**
 * Global Search (Firestore based)
 * - Supports multi-keyword AND search
 * - Applies canViewData() visibility checks
 * - Supports optional date range filter (YYYY-MM-DD)
 * - Supports scope filtering (advanced search checkboxes)
 */

function normalizeText(input: any): string {
  if (input == null) return "";
  return String(input).toLowerCase().trim().replace(/\s+/g, " ");
}

function normalizeForMatch(input: any): string {
  return normalizeText(input).replace(/\s/g, "");
}

function tokenizeQuery(q: string): string[] {
  return q
    .split(/[\s,，;；]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function toDateLike(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return null;
    const d = new Date(s.length >= 10 ? s.slice(0, 10) : s);
    return isNaN(d.getTime()) ? null : d;
  }

  // Firestore Timestamp (compat)
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }

  return null;
}

function inDateRange(doc: SearchDoc, from?: string, to?: string, dateFields: string[] = []): boolean {
  const hasFrom = !!from;
  const hasTo = !!to;
  if (!hasFrom && !hasTo) return true;

  const fromD = hasFrom ? new Date(from as string) : null;
  const toD = hasTo ? new Date(to as string) : null;
  if (fromD && isNaN(fromD.getTime())) return true;
  if (toD && isNaN(toD.getTime())) return true;

  let hasReadableDate = false;
  for (const f of dateFields) {
    const d = toDateLike((doc as any)[f]);
    if (!d) continue;
    hasReadableDate = true;

    if (fromD && d < fromD) continue;

    if (toD) {
      const end = new Date(toD);
      end.setHours(23, 59, 59, 999);
      if (d > end) continue;
    }

    return true;
  }

  // If doc has no readable date fields, do not filter it out.
  return !hasReadableDate;
}

function matchAllTokens(tokens: string[], haystack: string): boolean {
  const h = normalizeForMatch(haystack);
  return tokens.every((t) => h.includes(normalizeForMatch(t)));
}

function makeHit(args: {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  navKey: NavKey;
  sourceId?: string;
}): SearchHit {
  return {
    id: args.id,
    type: args.type,
    title: args.title,
    subtitle: args.subtitle,
    navKey: args.navKey,
    sourceId: args.sourceId,
  };
}

async function fetchRecentDocs(col: string, limitN: number, orderField?: string): Promise<SearchDoc[]> {
  const colRef = db.collection(col) as any;

  if (orderField) {
    try {
      const snap = await colRef.orderBy(orderField, "desc").limit(limitN).get();
      return snap.docs.map((d: any) => ({ id: d.id, ...(d.data() || {}) }));
    } catch (e) {
      console.warn("[GlobalSearch] fetch failed (orderBy)", col, e);
    }
  }

  try {
    const snap = await colRef.limit(limitN).get();
    return snap.docs.map((d: any) => ({ id: d.id, ...(d.data() || {}) }));
  } catch (e) {
    console.warn("[GlobalSearch] fetch failed", col, e);
    return [];
  }
}

function getCurrentAppUserLite(): { id: string; email: string } | null {
  const u = auth.currentUser;
  if (!u) return null;
  return { id: u.uid, email: u.email || "" };
}

function canSee(doc: any): boolean {
  const u = getCurrentAppUserLite();
  return canViewData(u as any, doc);
}

export async function runGlobalSearch(
  query: string,
  dateFrom?: string,
  dateTo?: string,
  options?: GlobalSearchOptions
): Promise<SearchHit[]> {
  const q = (query || "").trim();
  if (!q) return [];

  const tokens = tokenizeQuery(q);
  if (tokens.length === 0) return [];

  const scopeSet = new Set<SearchScope>((options?.scopes?.length ? options.scopes : ALL_SCOPES) as SearchScope[]);

  // All unchecked => return empty (UI will show message)
  if (options?.scopes && options.scopes.length === 0) return [];

  const LIMIT_PER_COLLECTION = 200;

  const promises = {
    contracts: scopeSet.has("contracts")
      ? fetchRecentDocs("contracts", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    legacyContracts: scopeSet.has("legacyContracts")
      ? fetchRecentDocs("legacyContracts", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    cases: scopeSet.has("cases")
      ? fetchRecentDocs("legal_cases", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    disputes: scopeSet.has("disputes")
      ? fetchRecentDocs("legal_disputes", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    brandAssets: scopeSet.has("brandAssets")
      ? fetchRecentDocs("brand_assets", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    lawyers: scopeSet.has("lawyers")
      ? fetchRecentDocs("legal_lawyers", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    templates: scopeSet.has("templates")
      ? fetchRecentDocs("contract_templates", LIMIT_PER_COLLECTION, "updatedAt")
      : Promise.resolve([] as SearchDoc[]),

    govInbound: scopeSet.has("govInbound")
      ? fetchRecentDocs("gov_inbound", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    governance: scopeSet.has("governance")
      ? fetchRecentDocs("governance_core_docs", LIMIT_PER_COLLECTION, "updatedAt")
      : Promise.resolve([] as SearchDoc[]),

    todos: scopeSet.has("todos")
      ? fetchRecentDocs("todos", LIMIT_PER_COLLECTION, "createdAt")
      : Promise.resolve([] as SearchDoc[]),

    approvalRequests: scopeSet.has("approvalRequests")
      ? fetchRecentDocs("approval_requests", LIMIT_PER_COLLECTION, "updatedAt")
      : Promise.resolve([] as SearchDoc[]),
  };

  const [
    contracts,
    legacyContracts,
    cases,
    disputes,
    brandAssets,
    lawyers,
    templates,
    govInbound,
    governance,
    todos,
    approvalRequests,
  ] = await Promise.all([
    promises.contracts,
    promises.legacyContracts,
    promises.cases,
    promises.disputes,
    promises.brandAssets,
    promises.lawyers,
    promises.templates,
    promises.govInbound,
    promises.governance,
    promises.todos,
    promises.approvalRequests,
  ]);

  const results: SearchHit[] = [];

  // 1) Contracts
  if (scopeSet.has("contracts")) {
    for (const c of contracts) {
      if (!canSee(c)) continue;
      if (!inDateRange(c, dateFrom, dateTo, ["createdAt", "updatedAt", "effectiveDate"])) continue;
      const title = c.title || c.contractName || c.name || "(未命名合約)";
      const number = c.number || c.contractNo || c.no || "";
      const cp = c.counterparty || c.party || c.vendor || "";
      const hay = `合約 contract ${title} ${number} ${cp} ${c.note || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `contracts_${c.id}`,
          type: "合約",
          title,
          subtitle: `${number}${cp ? `｜${cp}` : ""}`.replace(/^｜/, ""),
          navKey: "contracts",
          sourceId: c.id,
        })
      );
    }
  }

  // 2) Legacy Contracts
  if (scopeSet.has("legacyContracts")) {
    for (const c of legacyContracts) {
      if (!canSee(c)) continue;
      if (!inDateRange(c, dateFrom, dateTo, ["createdAt", "updatedAt", "effectiveDate"])) continue;
      const title = c.title || c.contractName || c.name || "(未命名舊合約)";
      const number = c.number || c.contractNo || c.no || "";
      const cp = c.counterparty || c.party || c.vendor || "";
      const hay = `舊合約 legacy ${title} ${number} ${cp} ${c.note || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `legacyContracts_${c.id}`,
          type: "舊合約",
          title,
          subtitle: `${number}${cp ? `｜${cp}` : ""}`.replace(/^｜/, ""),
          navKey: "legacyContracts",
          sourceId: c.id,
        })
      );
    }
  }

  // 3) Cases
  if (scopeSet.has("cases")) {
    for (const it of cases) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt", "incidentDate"])) continue;
      const title = it.title || it.caseTitle || "(未命名案件)";
      const no = it.caseNo || it.number || "";
      const cp = it.counterparty || it.party || "";
      const hay = `案件 case ${title} ${no} ${cp} ${it.summary || it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `case_${it.id}`,
          type: "案件",
          title,
          subtitle: `${no}${cp ? `｜${cp}` : ""}`.replace(/^｜/, ""),
          navKey: "legalMatters",
          sourceId: it.id,
        })
      );
    }
  }

  // 4) Disputes
  if (scopeSet.has("disputes")) {
    for (const it of disputes) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt", "incidentDate"])) continue;
      const title = it.title || it.disputeTitle || "(未命名爭議)";
      const no = it.disputeNo || it.number || "";
      const cp = it.counterparty || it.party || "";
      const hay = `爭議 dispute ${title} ${no} ${cp} ${it.summary || it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `dispute_${it.id}`,
          type: "爭議",
          title,
          subtitle: `${no}${cp ? `｜${cp}` : ""}`.replace(/^｜/, ""),
          navKey: "legalMatters",
          sourceId: it.id,
        })
      );
    }
  }

  // 5) Brand Assets
  if (scopeSet.has("brandAssets")) {
    for (const it of brandAssets) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt"])) continue;
      const title = it.name || it.assetName || it.title || "(未命名品牌資產)";
      const no = it.assetNo || it.number || "";
      const hay = `品牌 品牌資產 brand ${title} ${no} ${it.category || ""} ${it.notes || it.note || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `brand_${it.id}`,
          type: "品牌",
          title,
          subtitle: no ? `資產編號：${no}` : undefined,
          navKey: "brandAssets",
          sourceId: it.id,
        })
      );
    }
  }

  // 6) Lawyers  ✅關鍵修正：把「律師/顧問律師」塞進 hay，讓「賴 律師」AND 會命中
  if (scopeSet.has("lawyers")) {
    for (const it of lawyers) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt"])) continue;

      const title = it.name || it.lawyerName || it.contactName || it.firmName || "(未命名顧問律師)";
      const firm = it.firm || it.firmName || it.lawFirm || "";
      const specialty = it.specialty || it.expertise || it.practiceAreas || "";
      const phone = it.phone || it.tel || it.mobile || "";
      const email = it.email || "";

      const hay = `律師 顧問律師 法律事務所 lawyer ${title} ${firm} ${email} ${phone} ${specialty}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `lawyer_${it.id}`,
          type: "律師",
          title,
          subtitle: firm || undefined,
          navKey: "lawyerCoop",
          sourceId: it.id,
        })
      );
    }
  }

  // 7) Templates
  if (scopeSet.has("templates")) {
    for (const it of templates) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt"])) continue;
      const title = it.title || it.name || "(未命名範本)";
      const cat = it.category || it.templateType || "";
      const hay = `範本 合約範本 template ${title} ${cat} ${it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `tpl_${it.id}`,
          type: "範本",
          title,
          subtitle: cat ? `分類：${cat}` : undefined,
          navKey: "templates",
          sourceId: it.id,
        })
      );
    }
  }

  // 8) Gov Inbound
  if (scopeSet.has("govInbound")) {
    for (const it of govInbound) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt", "receivedDate", "docDate"])) continue;
      const title = it.subject || it.title || it.docTitle || "(未命名來文)";
      const no = it.docNo || it.refNo || it.letterNo || "";
      const agency = it.fromAgency || it.sender || it.department || "";
      const hay = `公文 來文 inbound ${title} ${no} ${agency} ${it.summary || it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `gov_${it.id}`,
          type: "公文",
          title,
          subtitle: `${no}${agency ? `｜${agency}` : ""}`.replace(/^｜/, ""),
          navKey: "govCorrespondence",
          sourceId: it.id,
        })
      );
    }
  }

  // 9) Governance Core Docs
  if (scopeSet.has("governance")) {
    for (const it of governance) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt", "publishedAt"])) continue;
      const title = it.title || it.name || "(未命名公司治理文件)";
      const agency = it.issuer || it.publisher || "";
      const hay = `公司治理 治理 governance ${title} ${agency} ${it.summary || it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `govcore_${it.id}`,
          type: "治理",
          title,
          subtitle: agency || undefined,
          navKey: "governanceCoreDocs",
          sourceId: it.id,
        })
      );
    }
  }

  // 10) Todos
  if (scopeSet.has("todos")) {
    for (const it of todos) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["createdAt", "updatedAt", "dueDate", "eventDate"])) continue;
      const title = it.title || it.todoTitle || it.summary || "(未命名待辦)";
      const cat = it.category || it.sourceType || "";
      const hay = `待辦 todo ${title} ${cat} ${it.detail || it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `todo_${it.id}`,
          type: "待辦",
          title,
          subtitle: cat ? `分類：${cat}` : undefined,
          navKey: "tasksAndLogs",
          sourceId: it.id,
        })
      );
    }
  }

  // 11) Approval Requests
  if (scopeSet.has("approvalRequests")) {
    for (const it of approvalRequests) {
      if (!canSee(it)) continue;
      if (!inDateRange(it, dateFrom, dateTo, ["submittedAt", "updatedAt", "createdAt"])) continue;
      const title = it.title || "(未命名簽核申請)";
      const module = it.module || "";
      const dept = it.department || it.applicantDept || "";
      const hay = `審查 合約審查 approval ${title} ${module} ${dept} ${it.description || ""}`;
      if (!matchAllTokens(tokens, hay)) continue;

      results.push(
        makeHit({
          id: `appr_${it.id}`,
          type: "審查",
          title,
          subtitle: `${module}${dept ? `｜${dept}` : ""}`.replace(/^｜/, ""),
          navKey: "contractReviews",
          sourceId: it.id,
        })
      );
    }
  }

  // Stable sort (simple + predictable)
  results.sort((a, b) => {
    const la = (a.title || "").length;
    const lb = (b.title || "").length;
    if (la !== lb) return la - lb;
    return a.id.localeCompare(b.id);
  });

  return results.slice(0, 20);
}
